// File: frontend/src/pages/ClassDetailPage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { classesAPI, studentsAPI, logsAPI, fileUploadAPI, getUploadErrorMessage } from '../services/api';
import Spinner from '../components/Spinner';
import EmptyState from '../components/EmptyState';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import { useLanguage } from '../LanguageContext';

// --- Sub-components for the tabs ---
const RosterTab = ({ roster, allStudents, onAdd, onRemove, onRemoveBulk }) => {
    const { t } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedToAdd, setSelectedToAdd] = useState(new Set());
    const [selectedToRemove, setSelectedToRemove] = useState(new Set());

    const rosterIds = useMemo(() => new Set(roster.map(s => s.id)), [roster]);

    const availableStudents = useMemo(() => {
        return allStudents.filter(s => !rosterIds.has(s.id));
    }, [rosterIds, allStudents]);

    const filteredAvailable = useMemo(() => {
        return availableStudents.filter(s => {
            const matchesCategory = categoryFilter === 'all' || (s.student_type || 'private') === categoryFilter;
            const term = searchTerm.trim().toLowerCase();
            const matchesSearch = !term || 
                (s.name && s.name.toLowerCase().includes(term)) ||
                (s.cram_school_name && s.cram_school_name.toLowerCase().includes(term)) ||
                (s.parent_name && s.parent_name.toLowerCase().includes(term)) ||
                (s.phone && s.phone.includes(term));
            return matchesCategory && matchesSearch;
        });
    }, [availableStudents, categoryFilter, searchTerm]);

    const handleToggleSelectToAdd = (studentId) => {
        setSelectedToAdd(prev => {
            const next = new Set(prev);
            if (next.has(studentId)) next.delete(studentId);
            else next.add(studentId);
            return next;
        });
    };

    const handleSelectAllFiltered = () => {
        const filteredIds = filteredAvailable.map(s => s.id);
        const allIn = filteredIds.length > 0 && filteredIds.every(id => selectedToAdd.has(id));
        if (allIn) {
            setSelectedToAdd(prev => {
                const next = new Set(prev);
                filteredIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            setSelectedToAdd(prev => {
                const next = new Set(prev);
                filteredIds.forEach(id => next.add(id));
                return next;
            });
        }
    };

    const handleConfirmAdd = () => {
        if (selectedToAdd.size === 0) return;
        onAdd(Array.from(selectedToAdd));
        setSelectedToAdd(new Set());
    };

    const handleToggleSelectToRemove = (studentId) => {
        setSelectedToRemove(prev => {
            const next = new Set(prev);
            if (next.has(studentId)) next.delete(studentId);
            else next.add(studentId);
            return next;
        });
    };

    const handleSelectAllRoster = () => {
        if (selectedToRemove.size === roster.length) {
            setSelectedToRemove(new Set());
        } else {
            setSelectedToRemove(new Set(roster.map(s => s.id)));
        }
    };

    const handleConfirmRemoveBulk = () => {
        if (selectedToRemove.size === 0) return;
        if (onRemoveBulk) {
            onRemoveBulk(Array.from(selectedToRemove));
            setSelectedToRemove(new Set());
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Current Enrolled Roster */}
            <div className="lg:col-span-5 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs flex flex-col">
                <div className="flex items-center justify-between gap-2 border-b pb-3 mb-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span>👥</span>
                            <span>Enrolled Roster ({roster.length})</span>
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Students actively enrolled in this class</p>
                    </div>

                    {selectedToRemove.size > 0 && (
                        <button
                            type="button"
                            onClick={handleConfirmRemoveBulk}
                            className="bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1"
                        >
                            <span>🗑️</span>
                            <span>Remove ({selectedToRemove.size})</span>
                        </button>
                    )}
                </div>

                {roster.length > 0 && (
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100 dark:border-gray-700 text-xs">
                        <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-600 dark:text-gray-400 select-none">
                            <input
                                type="checkbox"
                                checked={roster.length > 0 && selectedToRemove.size === roster.length}
                                onChange={handleSelectAllRoster}
                                className="w-3.5 h-3.5 rounded text-red-600 focus:ring-red-500 cursor-pointer"
                            />
                            <span>Select All ({roster.length})</span>
                        </label>
                        {selectedToRemove.size > 0 && (
                            <button
                                type="button"
                                onClick={() => setSelectedToRemove(new Set())}
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 cursor-pointer text-[11px]"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto max-h-[460px] pr-1 space-y-2">
                    {roster.length > 0 ? (
                        roster.map(student => {
                            const isSelected = selectedToRemove.has(student.id);
                            const isCram = student.student_type === 'cram_school';
                            return (
                                <div
                                    key={student.id}
                                    onClick={() => handleToggleSelectToRemove(student.id)}
                                    className={classNames('flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer select-none', {
                                        'bg-red-50/70 border-red-200 shadow-xs': isSelected,
                                        'bg-gray-50/80 border-gray-200 dark:border-gray-700 hover:bg-gray-100/80 hover:border-gray-300': !isSelected
                                    })}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}} // Handled by div onClick
                                            className="w-4 h-4 rounded text-red-600 focus:ring-red-500 cursor-pointer"
                                        />
                                        <div className="truncate">
                                            <span className="font-bold text-xs text-gray-900 dark:text-gray-100 block truncate">{student.name}</span>
                                            <span className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5">
                                                <span className={classNames('px-1.5 py-0.2 rounded-md font-medium text-[10px]', {
                                                    'bg-purple-100 text-purple-800': isCram,
                                                    'bg-emerald-100 text-emerald-800': !isCram
                                                })}>
                                                    {isCram ? `🏫 ${student.cram_school_name || 'Cram'}` : '🏠 Private'}
                                                </span>
                                                {student.parent_name && <span>• {student.parent_name}</span>}
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(student.id);
                                        }}
                                        className="text-gray-400 dark:text-gray-500 hover:text-red-600 p-1.5 rounded-lg hover:bg-white text-xs transition-colors cursor-pointer"
                                        title="Remove student from class"
                                    >
                                        ✕
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        <div className="py-12 text-center text-gray-400 dark:text-gray-500 bg-gray-50/50 rounded-xl border border-dashed">
                            <p className="text-3xl mb-1">📋</p>
                            <p className="text-xs font-semibold">No students currently enrolled.</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Use the multi-pick panel on the right to add students.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Column: Multi-Pick Available Students Protocol */}
            <div className="lg:col-span-7 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs flex flex-col">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 mb-3">
                    <div>
                        <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span>✨</span>
                            <span>{t('stu_multi_pick_title')}</span>
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('stu_multi_pick_desc')}
                        </p>
                    </div>

                    <button
                        type="button"
                        disabled={selectedToAdd.size === 0}
                        onClick={handleConfirmAdd}
                        className={classNames('px-4 py-2 rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer', {
                            'bg-emerald-600 text-white hover:bg-emerald-700 ring-2 ring-emerald-300': selectedToAdd.size > 0,
                            'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed': selectedToAdd.size === 0
                        })}
                    >
                        <span>➕</span>
                        <span>{t('stu_enroll_selected')} ({selectedToAdd.size})</span>
                    </button>
                </div>

                {/* Filter and Search Bar */}
                <div className="space-y-2.5 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 text-xs">🔍</span>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder={t('stu_search_placeholder')}
                                className="w-full pl-8 pr-8 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 cursor-pointer text-xs"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Category Filter Pills & Select All Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-100 dark:border-gray-700 text-xs">
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => setCategoryFilter('all')}
                                className={classNames('px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer', {
                                    'bg-gray-900 text-white': categoryFilter === 'all',
                                    'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200': categoryFilter !== 'all'
                                })}
                            >
                                {t('stu_all')} ({availableStudents.length})
                            </button>
                            <button
                                type="button"
                                onClick={() => setCategoryFilter('private')}
                                className={classNames('px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer', {
                                    'bg-emerald-600 text-white': categoryFilter === 'private',
                                    'bg-emerald-50 text-emerald-800 hover:bg-emerald-100': categoryFilter !== 'private'
                                })}
                            >
                                🏠 {t('stu_private')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCategoryFilter('cram_school')}
                                className={classNames('px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer', {
                                    'bg-purple-600 text-white': categoryFilter === 'cram_school',
                                    'bg-purple-50 text-purple-800 hover:bg-purple-100': categoryFilter !== 'cram_school'
                                })}
                            >
                                🏫 {t('stu_cram')}
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1.5 cursor-pointer font-bold text-gray-700 dark:text-gray-300 select-none bg-gray-50 dark:bg-gray-800/60 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100">
                                <input
                                    type="checkbox"
                                    checked={filteredAvailable.length > 0 && filteredAvailable.every(s => selectedToAdd.has(s.id))}
                                    onChange={handleSelectAllFiltered}
                                    className="w-3.5 h-3.5 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                />
                                <span>{t('stu_select_all')} ({filteredAvailable.length})</span>
                            </label>
                            {selectedToAdd.size > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedToAdd(new Set())}
                                    className="text-gray-400 dark:text-gray-500 hover:text-gray-600 cursor-pointer text-[11px] font-semibold"
                                >
                                    {t('stu_clear')} ({selectedToAdd.size})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Available Students Checklist */}
                <div className="flex-1 overflow-y-auto max-h-[400px] pr-1 space-y-2">
                    {filteredAvailable.length > 0 ? (
                        filteredAvailable.map(student => {
                            const isSelected = selectedToAdd.has(student.id);
                            const isCram = student.student_type === 'cram_school';
                            return (
                                <div
                                    key={student.id}
                                    onClick={() => handleToggleSelectToAdd(student.id)}
                                    className={classNames('flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer select-none', {
                                        'bg-emerald-50/80 border-emerald-300 ring-1 ring-emerald-200 shadow-2xs': isSelected,
                                        'bg-gray-50/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70 hover:border-gray-300': !isSelected
                                    })}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}} // Controlled by row onClick
                                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                        />
                                        <div className="truncate">
                                            <div className="flex items-center gap-2">
                                                <span className="font-extrabold text-xs text-gray-900 dark:text-gray-100">{student.name}</span>
                                                <span className={classNames('px-2 py-0.2 rounded-md font-bold text-[10px]', {
                                                    'bg-purple-100 text-purple-800': isCram,
                                                    'bg-emerald-100 text-emerald-800': !isCram
                                                })}>
                                                    {isCram ? `🏫 ${student.cram_school_name || 'Cram School'}` : '🏠 Private'}
                                                </span>
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                                {student.parent_name && <span>Parent: {student.parent_name}</span>}
                                                {student.phone && <span> • 📞 {student.phone}</span>}
                                                {student.family_name && <span> • 👨‍👩‍👧 {student.family_name}</span>}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleSelectToAdd(student.id);
                                        }}
                                        className={classNames('px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer', {
                                            'bg-emerald-600 text-white': isSelected,
                                            'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-emerald-50 hover:text-emerald-700': !isSelected
                                        })}
                                    >
                                        {isSelected ? '✓ Picked' : '+ Pick'}
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        <div className="py-12 text-center text-gray-400 dark:text-gray-500 bg-gray-50/50 rounded-xl border border-dashed">
                            <p className="text-3xl mb-1">🎉</p>
                            <p className="text-xs font-semibold">
                                {searchTerm ? 'No available students match your search.' : 'All available students are already enrolled!'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Bottom Bar */}
                {selectedToAdd.size > 0 && (
                    <div className="pt-3 mt-3 border-t flex items-center justify-between bg-emerald-50/70 p-3 rounded-xl border-emerald-200">
                        <span className="text-xs font-extrabold text-emerald-950">
                            {selectedToAdd.size} student{selectedToAdd.size > 1 ? 's' : ''} ready to enroll
                        </span>
                        <button
                            type="button"
                            onClick={handleConfirmAdd}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs px-4 py-2 rounded-xl shadow-xs transition-colors cursor-pointer"
                        >
                            Confirm & Enroll Now ➔
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const MaterialsTab = ({ materials, onAdd, onUpdate, onDelete }) => {
    const [editingMaterial, setEditingMaterial] = useState(null); // null or the material object
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [file, setFile] = useState(null);
    const [description, setDescription] = useState('');

    const handleSelectForEdit = (material) => {
        setEditingMaterial(material);
        setTitle(material.title);
        setUrl(material.url || ''); // Use url from existing material
        setFile(null); // Clear file input when editing
        setDescription(material.description || '');
    };

    const handleCancelEdit = () => {
        setEditingMaterial(null);
        setTitle('');
        setUrl('');
        setFile(null);
        setDescription('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Pass the entire state up to the parent to handle the upload, plus
        // the existing file_url so a plain edit (no new file picked)
        // doesn't wipe out the material's already-uploaded file.
        const materialData = { title, url, file, description, existing_file_url: editingMaterial?.file_url || null };
        if (editingMaterial) {
            onUpdate(editingMaterial.id, materialData);
        } else {
            onAdd(materialData);
        }
        handleCancelEdit(); // Reset the form after submission
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        // Material upload size limit (e.g., 10MB)
        if (selectedFile && selectedFile.size > 10 * 1024 * 1024) {
            toast.error("File is too large! Maximum size is 10MB.");
            e.target.value = null;
            return;
        }
        setFile(selectedFile);
        // If a file is selected, clear the URL input
        if (selectedFile) {
            setUrl('');
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <h3 className="text-xl font-semibold mb-2">Class Materials</h3>
                <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-lg border max-h-96 overflow-y-auto">
                    {materials.length > 0 ? (
                        <ul className="space-y-2">{materials.map(mat => (
                            <li key={mat.id} className="flex justify-between items-start p-2 hover:bg-gray-100 rounded gap-2">
                                <div className="min-w-0">
                                    {/* The link now prioritizes the uploaded file_url */}
                                    <a href={mat.file_url || mat.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                                        {mat.title}
                                    </a>
                                    {mat.description && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{mat.description}</p>
                                    )}
                                </div>
                                <div className="space-x-3 flex-shrink-0">
                                    <button onClick={() => handleSelectForEdit(mat)} className="text-sm text-blue-600">Edit</button>
                                    <button onClick={() => onDelete(mat.id)} className="text-sm text-red-600">Delete</button>
                                </div>
                            </li>
                        ))}</ul>
                    ) : <EmptyState message="No materials have been added." />}
                </div>
            </div>
            <div>
                <h3 className="text-xl font-semibold mb-2">{editingMaterial ? 'Edit Material' : 'Add New Material'}</h3>
                <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-lg border">
                    <div className="mb-2"><label>Title</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full border rounded p-2" required /></div>
                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Upload File (PDF/Image, etc.)</label>
                        <input 
                            type="file" 
                            onChange={handleFileChange} 
                            className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </div>
                    <div className="text-center text-gray-500 dark:text-gray-400 text-sm my-2">OR</div>
                    <div className="mb-4"><label>Link to External URL</label><input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full border rounded p-2" placeholder="https://..." /></div>
                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Instructions / Comment (optional)</label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className="w-full border rounded p-2 text-sm"
                            rows="3"
                            placeholder="e.g. Read pages 1-3 and complete the exercise before next class."
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Shown to the student alongside the material, explaining what to do with it.</p>
                    </div>
                    <div className="flex justify-end space-x-2">
                        {editingMaterial && <button type="button" onClick={handleCancelEdit}>Cancel</button>}
                        <button type="submit">{editingMaterial ? 'Save Changes' : 'Add Material'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CommunicationLogTab = ({ logs, students, onAdd }) => {
    const [studentId, setStudentId] = useState('');
    const [note, setNote] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!studentId) {
            toast.error('Please select a student.');
            return;
        }
        onAdd({ student_id: studentId, note });
        setStudentId('');
        setNote('');
    };

    const statusBadge = (log) => {
        if (log.status === 'pending_review') {
            return <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" title={log.flagged_reason}>🚩 Pending Review</span>;
        }
        if (log.status === 'rejected') {
            return <span className="text-[11px] font-bold bg-red-100 text-red-800 px-2 py-0.5 rounded-full" title={log.flagged_reason}>✕ Rejected</span>;
        }
        if (log.acknowledged_at) {
            return <span className="text-[11px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">✓ Seen by student</span>;
        }
        return <span className="text-[11px] font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Sent</span>;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <h3 className="text-xl font-semibold mb-2">Communication History</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Notes are delivered to the student (and parent via LINE, if linked) unless held for review.
                </p>
                <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-lg border max-h-96 overflow-y-auto">
                    {logs.length > 0 ? (
                        <ul className="space-y-4">
                            {logs.map(log => (
                                <li key={log.id} className="p-2 border-b">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="font-semibold">{log.student_name}</p>
                                        {statusBadge(log)}
                                    </div>
                                    <p className="text-gray-700 dark:text-gray-300 my-1">"{log.note}"</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        By {log.user_name} on {new Date(log.created_at).toLocaleString()}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    ) : <EmptyState message="No notes for students in this class yet." />}
                </div>
            </div>
            <div>
                <h3 className="text-xl font-semibold mb-2">Send New Note</h3>
                <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-lg border">
                    <div className="mb-2">
                        <label className="block text-sm font-medium">Student</label>
                        <select value={studentId} onChange={e => setStudentId(e.target.value)} className="w-full border rounded p-2" required>
                            <option value="">-- Select a Student --</option>
                            {students.map(student => <option key={student.id} value={student.id}>{student.name}</option>)}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium">Note</label>
                        <textarea value={note} onChange={e => setNote(e.target.value)} className="w-full border rounded p-2" rows="4" required placeholder="e.g., Great progress on vocabulary this week, keep it up!"></textarea>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Delivered to the student/parent - inappropriate language or off-platform contact requests will be held for admin review instead.</p>
                    </div>
                    <button type="submit" className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Send Note</button>
                </form>
            </div>
        </div>
    );
};


export default function ClassDetailPage() {
    const { classId } = useParams(); // Gets the class ID from the URL (e.g., '1')
    const [classDetails, setClassDetails] = useState(null);
    const [roster, setRoster] = useState([]); // Students in the class
    const [allStudents, setAllStudents] = useState([]); // All students in the school
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('roster'); // 'roster', 'materials', 'logs'
    const [materials, setMaterials] = useState([]);
    const [logs, setLogs] = useState([]);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            classesAPI.get(classId),
            classesAPI.getStudents(classId),
            studentsAPI.getAll(),
            classesAPI.getMaterials(classId),
            logsAPI.getForClass(classId), 
        ]).then(([classData, rosterData, allStudentsData, materialsData, logsData]) => {
            setClassDetails(classData);
            setRoster(rosterData);
            setAllStudents(allStudentsData);
            setMaterials(materialsData);
            setLogs(logsData);
        }).catch(err => {
            console.error("Failed to fetch class details:", err);
            toast.error("Could not load class data."); // Use toast for user feedback
        }).finally(() => setIsLoading(false));
    }, [classId]);

    useEffect(() => { 
        fetchData(); 
    }, [fetchData]);

    const handleAddStudents = async (studentIds) => {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        if (ids.length === 0) {
            toast.error("Please select at least one student to add.");
            return;
        }

        const loadingToast = toast.loading(`Enrolling ${ids.length} student${ids.length > 1 ? 's' : ''}...`);
        try {
            await classesAPI.addStudents(classId, ids);
            toast.success(`Successfully enrolled ${ids.length} student${ids.length > 1 ? 's' : ''}!`, { id: loadingToast });
            fetchData(); // Refresh all data from the server
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not enroll students.', { id: loadingToast });
        }
    };

    const handleRemoveStudent = async (studentId) => {
        if (window.confirm('Are you sure you want to remove this student from the class?')) {
            const loadingToast = toast.loading('Removing student...');
            try {
                await classesAPI.removeStudent(classId, studentId);
                toast.success('Student removed!', { id: loadingToast });
                setRoster(prev => prev.filter(s => s.id !== studentId)); 
            } catch (err) {
                toast.error(err.response?.data?.error || 'Could not remove student.', { id: loadingToast });
            }
        }
    };

    const handleRemoveStudentsBulk = async (studentIds) => {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        if (ids.length === 0) return;
        if (window.confirm(`Are you sure you want to remove ${ids.length} student${ids.length > 1 ? 's' : ''} from the class?`)) {
            const loadingToast = toast.loading(`Removing ${ids.length} student${ids.length > 1 ? 's' : ''}...`);
            try {
                await classesAPI.removeStudentsBulk(classId, ids);
                toast.success(`Removed ${ids.length} student${ids.length > 1 ? 's' : ''}!`, { id: loadingToast });
                setRoster(prev => prev.filter(s => !ids.includes(s.id)));
            } catch (err) {
                toast.error(err.response?.data?.error || 'Could not remove students.', { id: loadingToast });
            }
        }
    };

    const handleAddMaterial = async (materialData) => {
        const loadingToast = toast.loading('Saving material...');
        try {
            let fileUrl = null;
            // 1. If a file was included, upload it first
            if (materialData.file) {
                toast.loading('Uploading file...', { id: loadingToast });
                fileUrl = await fileUploadAPI.upload(materialData.file);
            }
            
            // 2. Prepare the final data and save the record to our database
            const finalData = { title: materialData.title, url: materialData.url, file_url: fileUrl, description: materialData.description, class_id: classId };
            const newMaterial = await classesAPI.addMaterial(finalData);
            
            setMaterials(prev => [newMaterial, ...prev]);
            toast.success('Material added!', { id: loadingToast });
        } catch (err) {
            toast.error(getUploadErrorMessage(err, 'Could not add material.'), { id: loadingToast });
        }
    };

    const handleUpdateMaterial = async (materialId, materialData) => {
        const loadingToast = toast.loading('Saving material...');
        try {
            // Keep the existing file_url unless a new file is uploaded, so
            // editing just the title/description doesn't wipe it out.
            let fileUrl = materialData.existing_file_url || null;
            // 1. If a new file was included, upload it
            if (materialData.file) {
                toast.loading('Uploading file...', { id: loadingToast });
                fileUrl = await fileUploadAPI.upload(materialData.file);
            }

            // 2. Prepare final data. If a file is present (new or existing), it overrides the URL.
            const finalData = {
                title: materialData.title,
                url: fileUrl ? null : materialData.url, // Clear manual URL if a file is present
                file_url: fileUrl,
                description: materialData.description
            };
            const updatedMaterial = await classesAPI.updateMaterial(materialId, finalData);
            
            setMaterials(prev => prev.map(m => m.id === updatedMaterial.id ? updatedMaterial : m));
            toast.success('Material updated!', { id: loadingToast });
        } catch (err) {
            toast.error(getUploadErrorMessage(err, 'Could not update material.'), { id: loadingToast });
        }
    };

    const handleDeleteMaterial = async (materialId) => {
        if (window.confirm("Are you sure you want to delete this material?")) {
            const deletePromise = classesAPI.deleteMaterial(materialId);
            toast.promise(deletePromise, {
                loading: 'Deleting material...',
                success: () => {
                    setMaterials(prev => prev.filter(m => m.id !== materialId));
                    return <b>Material deleted!</b>;
                },
                error: <b>Could not delete material.</b>
            });
        }
    };

    const handleAddLog = async (logData) => {
        const loadingToast = toast.loading('Sending note...');
        try {
            const newLog = await logsAPI.create(logData);
            if (newLog.status === 'pending_review') {
                toast(`Held for admin review: ${newLog.flagged_reason}`, { id: loadingToast, icon: '🚩', duration: 5000 });
            } else {
                toast.success('Note sent to student/parent!', { id: loadingToast });
            }
            // Instantly add the new log to the top of the list for better UX
            setLogs(prev => [newLog, ...prev]);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not add log.', { id: loadingToast });
        }
    };

    if (isLoading) {
        return <div className="p-10"><Spinner /></div>;
    }

    const tabButtonClass = (tabName) => classNames('py-2 px-4 border-b-2 font-medium', {
        'border-blue-600 text-blue-600': activeTab === tabName,
        'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 hover:text-gray-700': activeTab !== tabName
    });

    return (
        <div>
            <div className="mb-4">
                <Link to="/classes" className="text-blue-600 hover:underline">&larr; Back to All Classes</Link>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-2xl font-bold">{classDetails?.name}</h2>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${classDetails?.class_type === 'cram_school' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {classDetails?.class_type === 'cram_school' 
                                ? `🏫 Cram School: ${classDetails?.cram_school_name || ''} ${parseFloat(classDetails?.hourly_wage) > 0 ? `(NT$${classDetails?.hourly_wage}/hr)` : ''}`
                                : `🏠 Private Tutoring (${classDetails?.format === 'online' ? 'Online' : 'In-Person'})`
                            }
                        </span>
                        {classDetails?.line_group_id ? (
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1" title={`LINE Group ID: ${classDetails.line_group_id}`}>
                                🟢 LINE Group Connected
                            </span>
                        ) : (
                            <span className="px-2 py-0.5 rounded text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700" title="To link, add the LINE Bot to the group and send: !link [Class Name]">
                                💬 LINE: Not linked (Send <code className="font-mono bg-gray-200 px-1 rounded text-[11px]">!link {classDetails?.name}</code> in group)
                            </span>
                        )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mt-0.5">Teacher: {classDetails?.teacher_name} | Time: {classDetails?.day} {classDetails?.start_time?.slice(0, 5)} - {classDetails?.end_time?.slice(0, 5)}</p>
                </div>
            </div>
            <div className="flex border-b mb-4 overflow-x-auto no-scrollbar whitespace-nowrap">
                <button onClick={() => setActiveTab('roster')} className={tabButtonClass('roster')}>Roster</button>
                <button onClick={() => setActiveTab('materials')} className={tabButtonClass('materials')}>Materials</button>
                <button onClick={() => setActiveTab('logs')} className={tabButtonClass('logs')}>Notes to Students</button>
            </div>

            <div>
                {activeTab === 'roster' && (
                    <RosterTab 
                        roster={roster} 
                        allStudents={allStudents} 
                        onAdd={handleAddStudents} 
                        onRemove={handleRemoveStudent} 
                        onRemoveBulk={handleRemoveStudentsBulk}
                    />
                )}
                {activeTab === 'materials' && <MaterialsTab materials={materials} onAdd={handleAddMaterial} onUpdate={handleUpdateMaterial} onDelete={handleDeleteMaterial}/>}
                {activeTab === 'logs' && <CommunicationLogTab logs={logs} students={roster} onAdd={handleAddLog} />}
            </div>
        </div>
    );
}